import { CommonModule, Location } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, computed, inject, NgZone, OnInit, Signal, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { DateTime } from 'luxon';
import { debounceTime, merge, take } from 'rxjs';
import * as XLSX from 'xlsx';
import { AppEvent } from '../../../models/app-event';
import { AttendanceType, ValidityStatus } from '../../../models/enums';
import { EventService } from '../../../services/event.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { TableStateService } from '../../../services/table-state.service';
import { getGridTemplate, mapObject, MY_DATE_FORMATS } from '../../../utils/utils';
import { AdvancedSelectComponent, AdvancedSelectValue, createAdvancedSelectValue } from '../../misc/advanced-select/advanced-select.component';
import { ColumnSelectorDialogComponent } from '../../misc/column-selector-dialog/column-selector-dialog.component';
import { enumToSortedNestedOptions, mapStringArrayToSortedNestedOptions, mapToNestedOptions } from '../../misc/nested-options';


interface EventFilters {
  textFilter: string;
  activeRange: {
    start: DateTime | null;
    end: DateTime | null;
  }
  topics: AdvancedSelectValue;
  eventTypes: AdvancedSelectValue;
  responsiblePersons: AdvancedSelectValue;
}


@Component({
  imports: [
    AdvancedSelectComponent,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
  providers: [
    provideLuxonDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
  ],
  templateUrl: './events.component.html',
  styleUrl: './events.component.scss'
})
export class EventsComponent implements OnInit, AfterViewInit {
  readonly ValidityStatus = ValidityStatus;
  tableId = 'events'
  searchText = '';
  loading: boolean = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('filtersPanel') filtersPanel!: MatExpansionPanel;

  dataSource = new MatTableDataSource<AppEvent>();
  allColumns = [
    { key: 'valid', label: 'Valid', type: 'icon' },
    { key: 'name', label: 'Name', type: 'longtext' },
    { key: 'date', label: 'Start date', type: 'text' },
    { key: 'endDate', label: 'End date', type: 'text' },
    { key: 'attendanceType', label: 'Attendance type', type: 'text' },
    { key: 'eventTypes', label: 'Event types', type: 'chips' },
    { key: 'topics', label: 'Topics of interest', type: 'chips' },
    { key: 'projects', label: 'Projects', type: 'chips' },
    { key: 'responsiblePerson', label: 'Responsible person', type: 'text' },
    { key: 'lastModifiedOn', label: 'Last modified on', type: 'text' },
  ];
  columnLabelMap: Record<string, string> = {};

  defaultSelectedColumns: string[] = ['valid', 'name', 'date', 'endDate', 'eventTypes', 'topics'];
  defaultColumnsOrder: string[] = this.allColumns.map(v => v.key);
  selectedColumns: string[] = this.defaultSelectedColumns;
  pageEvent: PageEvent | undefined;

  readonly showAllOrAny: Signal<boolean> = signal(false);

  readonly refDataService = inject(ReferenceDataService);
  readonly allTopics = this.refDataService.getGeneralTopicsOfInterest();
  readonly allTopicOptions = computed(() => mapToNestedOptions(this.allTopics(), true));
  readonly allEventTypes = this.refDataService.getEventTypes();
  readonly allEventTypeOptions = computed(() => mapToNestedOptions(this.allEventTypes(), true));
  readonly allAttendanceTypes = Object.values(AttendanceType);
  readonly allAttendanceTypeOptions = enumToSortedNestedOptions(AttendanceType);
  readonly allResponsiblePersons = signal<string[]>([]);
  readonly allResponsiblePersonOptions = computed(() => mapStringArrayToSortedNestedOptions(this.allResponsiblePersons()))

  filterForm: FormGroup;
  filters: EventFilters = this.getDefaultFilters();
  filtersExpanded: boolean = true;

  resetCounter = 0; // used to reset the filter form

  constructor(private eventService: EventService, private tableStateService: TableStateService,
    private dialog: MatDialog, private snackBar: MatSnackBar, private router: Router,
    private location: Location, private cdr: ChangeDetectorRef, private zone: NgZone,
    private formBuilder: FormBuilder) {
    this.filterForm = {} as FormGroup;
  }

  ngOnInit(): void {
    this.columnLabelMap = Object.fromEntries(
      this.allColumns.map(col => [col.key, col.label])
    );
    this.loadEvents();

    // Display and clear any navigation message
    const state = this.location.getState() as { message?: string };
    if (state?.message) {
      this.snackBar.open(state.message, 'Close', {
        duration: 3000,
        panelClass: ['snackbar-success']
      });
      // Clear the message from history so it doesn't persist on reload
      history.replaceState({}, document.title, location.pathname);
    }

    this.filterForm = this.formBuilder.nonNullable.group({
      textFilter: '',
      responsiblePersons: { selectedValues: [], matchAll: false },
      eventTypes: { selectedValues: [], matchAll: false },
      topics: { selectedValues: [], matchAll: false },
      // DateTime stored as ISO strings for better portability
      activeRange: this.formBuilder.group({
        start: new FormControl<string | null>(null),
        end: new FormControl<string | null>(null),
      })
    });

  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = this.filterFunction;


    const saved = this.tableStateService.getState<EventFilters>(this.tableId);
    if (saved) {
      this.zone.onStable.pipe(take(1)).subscribe(() => {
        this.paginator.pageIndex = saved.pageIndex;
        this.paginator.pageSize = saved.pageSize;
        this.sort.active = saved.sortActive;
        this.sort.direction = saved.sortDirection;
        this.filterForm.patchValue(saved.filters ? saved.filters : this.getDefaultFilters());
        this.filtersExpanded = saved.filtersExpanded;
        this.selectedColumns = saved.selectedColumnKeys ?? this.defaultSelectedColumns;
        if (saved.allColumnKeys) {
          this.allColumns.sort(
            (a, b) => saved.allColumnKeys.indexOf(a.key) - saved.allColumnKeys.indexOf(b.key)
          );
        }
        this.cdr.detectChanges();
      });
    }
    merge(
      this.filtersPanel.expandedChange,
      this.paginator.page,
      this.sort.sortChange,
      this.filterForm.valueChanges.pipe(debounceTime(300))
    ).subscribe(() => {
      this.saveState();
    });
    this.filterForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe(filters => {
        if (filters)
          this.dataSource.filter = JSON.stringify(filters);
      });
  }

  /**
   * creates the template for the table columns
   */
  get gridTemplate() {
    return getGridTemplate(this.selectedColumns, this.allColumns);
  }


  /**
   * 
   */
  get activeRange(): FormGroup {
    return this.filterForm?.get('activeRange') as FormGroup
  }

  /**
   * 
   */

  loadEvents(): void {
    this.loading = true;
    this.eventService.getAllEvents().subscribe({
      next: events => {
        this.dataSource.data = events;
        const uniquePersons: string[] = [];
        for (const e of events) {
          if ((e.responsiblePerson) && (!uniquePersons.includes(e.responsiblePerson))) {
            uniquePersons.push(e.responsiblePerson);
          }
        }
        uniquePersons.sort((a, b) => a.localeCompare(b));
        this.allResponsiblePersons.set(uniquePersons);
        this.loading = false;
      },
      error: msg => {
        console.error('Failed to load events:', msg);
        this.snackBar.open('Failed to load events', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
        this.loading = false;
      }
    });
  }

  /**
   * 
   */

  saveState() {
    const state = {
      pageIndex: this.paginator?.pageIndex ?? 0,
      pageSize: this.paginator?.pageSize ?? 10,
      sortActive: this.sort?.active,
      sortDirection: this.sort?.direction,
      filters: this.filterForm.getRawValue(),
      filtersExpanded: this.filtersExpanded,
      allColumnKeys: this.allColumns.map(c => (c.key)),
      selectedColumnKeys: this.selectedColumns
    }
    this.tableStateService.setState(this.tableId, state);
  }

  goToDetail(event: AppEvent): void {
    this.saveState();
    this.router.navigate(['/events', event.id]).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  add(): void {
    this.saveState();
    this.router.navigate(['/events', 'new']);
  }

  delete(id: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Event',
        message: 'Are you sure you want to delete this event?'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.eventService.delete(id).subscribe({
          next: () => {
            this.loadEvents();
            this.snackBar.open('Event deleted', 'Close', { duration: 3000 });
          },
          error: msg => {
            this.snackBar.open('Failed to delete event', 'Close', { duration: 3000 });
            console.error('Delete failed:', msg)
          }
        });
      }
    });
  }

  export(): void {
    const data: any[] = [];
    this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort!).forEach(d => {
      data.push(mapObject(d, this.selectedColumns));
    });

    const wb = XLSX.utils.book_new();
    wb.Props ??= {};
    wb.Props.Title = 'List of events';

    const ws = XLSX.utils.json_to_sheet(data, { cellDates: true, dateNF: 'dd/mm/YYYY' });
    const range = XLSX.utils.decode_range(ws['!ref']!);
    let col_index = XLSX.utils.decode_col("W");
    for (let row = range.s.r + 1; row <= range.e.r; ++row) {
      const ref = XLSX.utils.encode_cell({ r: row, c: col_index })
      if (ws[ref]?.t === 'd') {
        ws[ref].z = 'mm/YYYY';
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, "Events");
    XLSX.writeFileXLSX(wb, "events.xlsx");
  }

  openColumnSelector(): void {
    const dialogRef = this.dialog.open(ColumnSelectorDialogComponent, {
      data: {
        allColumns: this.allColumns,
        selected: [...this.selectedColumns],
        defaultSelection: [...this.defaultSelectedColumns],
        defaultOrder: [...this.defaultColumnsOrder]
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.selectedColumns = result.selected;
        this.allColumns = result.allColumns;
        this.saveState();
      }
    });
  }

  getDefaultFilters(): EventFilters {
    return {
      textFilter: '',
      activeRange: {
        start: null,
        end: null
      },
      responsiblePersons: createAdvancedSelectValue(),
      eventTypes: createAdvancedSelectValue(),
      topics: createAdvancedSelectValue(),
    };
  }

  // getDefaultFilters(): EventFilters {
  //   return {
  //     textFilter: '',
  //     activeRange: {
  //       start: null,
  //       end: null
  //     },
  //     responsiblePersonsFilter: [],
  //     responsiblePersonsMatchAll: false,
  //     eventTypesFilter: [],
  //     eventTypesMatchAll: false,
  //     topicsFilter: [],
  //     topicsMatchAll: false,
  //   };
  // }

  clearAllFilters() {
    console.debug('clear all filters')
    this.resetCounter++; // increment → triggers ngOnChanges in children
    this.filterForm.reset();
  }

  toggleFilterMatch(fieldName: keyof EventFilters) {
    this.filterForm.get(fieldName)?.setValue(!this.filterForm.get(fieldName)?.value);
  }

  togglePanel(event: MouseEvent) {
    event.stopPropagation(); // prevent header click from toggling
    this.filtersExpanded = !this.filtersExpanded;
  }

  /**
   * 
   * @param data the table row to filter
   * @param filter 
   * @returns 
   */

  // I have to defined it this way so that the this context is preserved

  filterFunction = (data: AppEvent, filter: string) => {
    const rawFilters = JSON.parse(filter);
    const filters: EventFilters = {
      ...rawFilters,
      activeRange: {
        start: rawFilters.activeRange?.start ? DateTime.fromISO(rawFilters.activeRange.start) : null,
        end: rawFilters.activeRange?.end ? DateTime.fromISO(rawFilters.activeRange.end) : null
      },
    };

    if (filters?.textFilter) {
      const searchStr = filters.textFilter.toLowerCase();
      const inName = data.name?.toLowerCase().includes(searchStr);
      const inDescription = data.description?.toLowerCase().includes(searchStr);
      if (!inName && !inDescription) {
        return false;
      }
    }

    // For events, only consider the start date
    const startDate = data.date ? DateTime.fromISO(data.date) : null;
    // const endDate = data.endDate ? DateTime.fromISO(data.endDate) : null;
    const filterStart = filters?.activeRange.start ?? null;
    const filterEnd = filters?.activeRange.end ?? null;
    if (startDate) {
      if (filterStart && filterEnd) {
        if ((startDate < filterStart) || (startDate > filterEnd)) {
          return false;
        }
      } else if ((filterStart && (startDate < filterStart)) || (filterEnd && (startDate < filterEnd))) {
        return false;
      }
    }

    if (filters?.topics?.selectedValues && filters.topics?.selectedValues.length > 0) {
      const intersection = data.topics.filter(item => filters.topics?.selectedValues.includes(String(item.id)));
      if (filters.topics?.matchAll) {
        if (intersection.length < filters.topics?.selectedValues.length) {
          return false
        }
      } else if (intersection.length === 0) {
        return false;
      }
    }
    if (filters?.eventTypes?.selectedValues && filters.eventTypes?.selectedValues.length > 0) {
      const intersection = data.eventTypes.filter(item => filters.eventTypes?.selectedValues.includes(String(item.id)));
      if (filters.eventTypes?.matchAll) {
        if (intersection.length < filters.eventTypes?.selectedValues.length) {
          return false
        }
      } else if (intersection.length === 0) {
        return false;
      }
    }
    if (filters?.responsiblePersons?.selectedValues && filters.responsiblePersons?.selectedValues.length > 0) {
      if (!data.responsiblePerson)
        return false;
      // data.reponsiblePerson is the name (string) but the filter returns the id, so I have to lookup the id in 
      // allResponsiblePersonOptions
      const responsiblePerson = this.allResponsiblePersonOptions().filter(item => item.label === data.responsiblePerson);
      const intersection = responsiblePerson.filter(item => filters.responsiblePersons?.selectedValues.includes(String(item.value)));
      if (filters.eventTypes?.matchAll) {
        if (intersection.length < filters.responsiblePersons?.selectedValues.length) {
          return false
        }
      } else if (intersection.length === 0) {
        return false;
      }
    }

    return true;
  }
}
